import { defineComponent, PropType, watch, ref, computed, warn, unref, Ref, ComputedRef, reactive, SetupContext } from "vue";
const __DEV__ = true;

export type MaybeRef<T = unknown> = T | Ref<T>;
export type MaybeRefOrGetter<T = unknown> = MaybeRef<T> | (() => T);
function toValue<T>(source: MaybeRefOrGetter<T>): T {
    // @ts-expect-error: source not callable
    return typeof source === "function" ? source() : unref(source);
}

export interface UsePromiseResult<T = unknown> {
    isPending: ComputedRef<boolean>;
    isResolved: Ref<boolean>;
    isRejected: Ref<boolean>;
    isDelayElapsed: Ref<boolean>;
    error: Ref<Error | undefined | null>;
    data: Ref<T | undefined | null>;
}

export const Promised = /*#__PURE__*/ defineComponent(
    <T>(props: { promise: Promise<T> | null | undefined; pendingDelay: number | string | undefined | null }, { slots }: SetupContext) => {
        const isRejected = ref(false);
        const isResolved = ref(false);
        const isPending = computed(() => !isRejected.value && !isResolved.value);
        const isDelayElapsed = ref(false);
        const error = ref<Error | undefined | null>();
        const data = ref<T | null | undefined>();

        let timerId: ReturnType<typeof setTimeout> | undefined | null;

        // Because passing watch a bare promise causes it to be resolved without a catch
        const promiseWrapper = computed(() => ({ promise: props.promise }));

        watch(
            () => promiseWrapper.value,
            ({ promise: newPromise }) => {
                isRejected.value = false;
                isResolved.value = false;
                error.value = null;
                if (!newPromise) {
                    data.value = null;
                    if (timerId) clearTimeout(timerId);
                    timerId = null;
                    return;
                }

                const pendingDelayNumber = props.pendingDelay == null ? 200 : Number(toValue(props.pendingDelay)) || 0;
                if (pendingDelayNumber > 0) {
                    isDelayElapsed.value = false;
                    if (timerId) clearTimeout(timerId);
                    timerId = setTimeout(() => {
                        isDelayElapsed.value = true;
                    }, pendingDelayNumber);
                } else {
                    isDelayElapsed.value = true;
                }

                newPromise.then(
                    newData => {
                        // ensure we are dealing with the same promise
                        if (newPromise === toValue(props.promise)) {
                            //console.log("Promise has NOT changed: 1");
                            data.value = newData;
                            isResolved.value = true;
                        } else {
                            //console.log("Promise has changed: 1");
                        }
                    },
                    err => {
                        // ensure we are dealing with the same promise
                        if (newPromise === toValue(props.promise)) {
                            //console.log("Promise has NOT changed: 2");
                            error.value = err;
                            isRejected.value = true;
                        } else {
                            //console.log("Promise has changed: 1");
                        }
                    },
                );
            },
            { immediate: true },
        );

        const promiseState = reactive({ isPending, isRejected, isResolved, isDelayElapsed, error, data });

        return () => {
            if ("combined" in slots) {
                return slots.combined!(promiseState);
            }

            const [slotName, slotData] = promiseState.isRejected ? ["rejected", promiseState.error] : !promiseState.isPending ? ["default", promiseState.data] : promiseState.isDelayElapsed ? ["pending", promiseState.data] : [null];

            if (__DEV__ && slotName && !slots[slotName]) {
                warn(`(vue-promised) Missing slot "${slotName}" in Promised component. Pass an empty "${slotName}" slot or use the "combined" slot to remove this warning. This will fail in production.`);
                return null;
            }

            return slotName && slots[slotName]!(slotData);
        };
    },
    {
        name: "Promised",
        props: {
            promise: {} as PropType<Promise<unknown> | null | undefined>,
            // validator: p =>
            //   p && typeof (p as any).then === 'function' && typeof (p as any).catch === 'function',

            pendingDelay: {} as PropType<number | string | null | undefined>,
        },
    },
);
